import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { NewApiKeyDialog } from './NewApiKeyDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const KEY_BLOCKED_ROLES = ['admin', 'lead_feeder'];
const dt = (v) => (v ? new Date(v).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–');

const keyStatus = (k) => {
  if (k.revoked_at) return ['Unieważniony', 'bg-red-500/15 text-red-400'];
  if (k.expires_at && new Date(k.expires_at) <= new Date()) return ['Wygasł', 'bg-amber-500/15 text-amber-500'];
  return ['Aktywny', 'bg-emerald-500/15 text-emerald-500'];
};

const KeyRow = ({ k, onRevoke }) => {
  const [label, cls] = keyStatus(k);
  const active = label === 'Aktywny';
  return (
    <TableRow className="border-[var(--crm-border)]" data-testid={`api-key-row-${k.id}`}>
      <TableCell className="font-medium text-[var(--crm-text-primary)]">{k.name}</TableCell>
      <TableCell className="text-[var(--crm-text-secondary)] text-xs">{k.user ? `${k.user.initials} – ${k.user.email}` : '(usunięte konto)'}</TableCell>
      <TableCell><Badge variant="outline" className="text-xs">{k.scope === 'read_write' ? 'Odczyt i zapis' : 'Odczyt'}</Badge></TableCell>
      <TableCell className="font-mono text-xs text-[var(--crm-text-secondary)]">{k.key_prefix}…</TableCell>
      <TableCell className="text-xs text-[var(--crm-text-muted)] whitespace-nowrap">{dt(k.created_at)}</TableCell>
      <TableCell className="text-xs text-[var(--crm-text-muted)] whitespace-nowrap">{dt(k.last_used_at)}{k.last_used_ip ? <div className="font-mono">{k.last_used_ip}</div> : null}</TableCell>
      <TableCell className="text-xs text-[var(--crm-text-muted)] whitespace-nowrap">{k.expires_at ? dt(k.expires_at) : 'Bez limitu'}</TableCell>
      <TableCell><Badge className={`${cls} text-xs`} data-testid={`api-key-status-${k.id}`}>{label}</Badge></TableCell>
      <TableCell>
        {active && (
          <Button size="sm" variant="outline" className="text-red-400 border-red-500/40 hover:bg-red-500/10" onClick={() => onRevoke(k)} data-testid={`api-key-revoke-btn-${k.id}`}>
            Unieważnij
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export const ApiKeysSettings = () => {
  const [keys, setKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toRevoke, setToRevoke] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async () => {
    try {
      const [k, u] = await Promise.all([axios.get(`${API_URL}/api-keys`), axios.get(`${API_URL}/users`)]);
      setKeys(k.data);
      setUsers(u.data.filter((x) => x.is_active !== false && !KEY_BLOCKED_ROLES.includes(x.role)));
    } catch { toast.error('Nie udało się pobrać kluczy API'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const visible = showRevoked ? keys : keys.filter((k) => !k.revoked_at);
  const revokedCount = keys.length - keys.filter((k) => !k.revoked_at).length;

  const revoke = async () => {
    try {
      await axios.post(`${API_URL}/api-keys/${toRevoke.id}/revoke`);
      toast.success('Klucz unieważniony');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    setToRevoke(null);
  };

  return (
    <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="api-keys-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/15 flex items-center justify-center"><KeyRound className="w-5 h-5 text-[#FF6B35]" /></div>
          <div>
            <CardTitle className="text-lg">Klucze API</CardTitle>
            <CardDescription className="text-[var(--crm-text-secondary)]">Dostęp dla zewnętrznych integracji (nagłówek X-API-Key). Klucz ma uprawnienia przypisanego konta.</CardDescription>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white shrink-0" data-testid="api-key-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nowy klucz
        </Button>
      </CardHeader>
      <CardContent>
        {revokedCount > 0 && (
          <label className="flex items-center gap-2 mb-3 text-sm text-[var(--crm-text-secondary)] cursor-pointer w-fit">
            <Switch checked={showRevoked} onCheckedChange={setShowRevoked} className="data-[state=checked]:bg-[#FF6B35]" data-testid="api-keys-show-revoked-switch" />
            Pokaż unieważnione ({revokedCount})
          </label>
        )}
        <div className="overflow-x-auto rounded-md border border-[var(--crm-border)]">
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--crm-border)]">
                {['Nazwa', 'Konto', 'Zakres', 'Prefiks', 'Utworzono', 'Ostatnie użycie', 'Wygasa', 'Status', ''].map((h) => (
                  <TableHead key={h} className="text-[var(--crm-text-secondary)] whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-[var(--crm-text-muted)] py-8" data-testid="api-keys-loading">Ładowanie…</TableCell></TableRow>
              ) : visible.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-[var(--crm-text-muted)] py-8" data-testid="api-keys-empty">Brak kluczy API</TableCell></TableRow>
              ) : visible.map((k) => <KeyRow key={k.id} k={k} onRevoke={setToRevoke} />)}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <NewApiKeyDialog open={dialogOpen} onOpenChange={setDialogOpen} users={users} onCreated={load} />
      <AlertDialog open={!!toRevoke} onOpenChange={(o) => !o && setToRevoke(null)}>
        <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="api-key-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unieważnić klucz „{toRevoke?.name}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--crm-text-secondary)]">Integracja korzystająca z tego klucza natychmiast straci dostęp. Tej operacji nie można cofnąć.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="api-key-revoke-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} className="bg-red-600 hover:bg-red-700 text-white" data-testid="api-key-revoke-confirm">Unieważnij</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
